"use client";

import { SignInButton, useAuth } from "@clerk/nextjs";
import { AlertCircle, BookMarkedIcon, Gamepad2, Menu, Video, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useIntlayer } from "next-intlayer";
import { useEffect, useState } from "react";
import DarkModeToggle from "./DarkModeToggle";
import LocaleSwitcher from "./LocaleSwitcher";
import { SearchInput } from "./SearchInput";
import UserDropdown from "./UserDropdown";
import { Button } from "./ui/button";

const MEET_URL = process.env.NEXT_PUBLIC_MEET_URL ?? "http://localhost:6080";
const SPACE_URL = process.env.NEXT_PUBLIC_SPACE_URL ?? "http://localhost:3002";

export default function Header({ showMeet = true }: { showMeet?: boolean }) {
  const { isSignedIn } = useAuth();
  const { myCourses, learningSpace, meet, signIn, inAppWarning } = useIntlayer("header");
  const [menuOpen, setMenuOpen] = useState(false);
  const [isInApp, setIsInApp] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isWebView = /FBAN|FBAV|Line|Instagram|Twitter|MicroMessenger|WebView/i.test(ua);
    setIsInApp(isWebView);
  }, []);

  const renderInAppWarning = () => {
    if (!isInApp || isDismissed || isSignedIn) return null;
    return (
      <div className="fixed top-16 left-0 right-0 z-40 bg-amber-500/10 border-b border-amber-500/20 backdrop-blur-md text-amber-600 dark:text-amber-400 py-2.5 px-4 animate-in fade-in slide-in-from-top-1 duration-200">
        <div className="container mx-auto flex items-center justify-between gap-4 text-xs md:text-sm font-medium">
          <div className="flex items-center gap-2 text-left">
            <AlertCircle className="w-4 h-4 shrink-0 text-amber-500" />
            <span>{inAppWarning}</span>
          </div>
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className="p-1 hover:bg-amber-500/15 rounded-full transition-colors shrink-0 cursor-pointer"
            aria-label="Dismiss warning"
          >
            <X className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4">
          {/* Main row */}
          <div className="flex h-16 items-center justify-between gap-2">
            {/* Left: Logo + desktop search */}
            <div className="flex items-center gap-4 min-w-0">
              <Link
                href="/"
                prefetch={false}
                className="flex items-center space-x-2 hover:opacity-90 transition-opacity shrink-0"
              >
                <Image src="/logo.svg" alt="SomeMath logo" width={28} height={28} />
                <span className="text-xl font-bold bg-gradient-to-r from-primary/90 to-primary bg-clip-text text-transparent">
                  SomeMath
                </span>
              </Link>

              <div className="hidden md:flex">
                <SearchInput />
              </div>
            </div>

            {/* Desktop: Nav items */}
            <div className="hidden md:flex items-center gap-3">
              <nav className="flex items-center gap-2">
                <a
                  href={SPACE_URL}
                  className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors border border-border rounded-md px-3 py-1.5"
                >
                  <Gamepad2 className="h-4 w-4 shrink-0" />
                  <span>{learningSpace}</span>
                </a>
                <Link
                  prefetch={false}
                  href="/my-courses"
                  className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors border border-border rounded-md px-3 py-1.5"
                >
                  <BookMarkedIcon className="h-4 w-4 shrink-0" />
                  <span>{myCourses}</span>
                </Link>
                {showMeet && (
                  <a
                    href={MEET_URL}
                    className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors border border-border rounded-md px-3 py-1.5"
                  >
                    <Video className="h-4 w-4 shrink-0" />
                    <span>{meet}</span>
                  </a>
                )}
              </nav>

              <LocaleSwitcher />
              <DarkModeToggle />

              {isSignedIn ? (
                <UserDropdown />
              ) : (
                <SignInButton mode="modal">
                  <Button variant="outline" size="sm">
                    {signIn}
                  </Button>
                </SignInButton>
              )}
            </div>

            {/* Mobile: Auth + Hamburger */}
            <div className="flex md:hidden items-center gap-2">
              {isSignedIn ? (
                <UserDropdown />
              ) : (
                <SignInButton mode="modal">
                  <Button variant="outline" size="sm">
                    {signIn}
                  </Button>
                </SignInButton>
              )}
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Toggle menu"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Mobile dropdown menu */}
          {menuOpen && (
            <div className="md:hidden border-t border-border py-4 flex flex-col gap-4">
              <SearchInput className="max-w-none" />
              <nav className="flex flex-col px-1">
                <a
                  href={SPACE_URL}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  <Gamepad2 className="h-4 w-4" />
                  <span>{learningSpace}</span>
                </a>
                <Link
                  prefetch={false}
                  href="/my-courses"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  <BookMarkedIcon className="h-4 w-4" />
                  <span>{myCourses}</span>
                </Link>
                {showMeet && (
                  <a
                    href={MEET_URL}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                  >
                    <Video className="h-4 w-4" />
                    <span>{meet}</span>
                  </a>
                )}
              </nav>
              <div className="flex items-center gap-3 pt-2 border-t border-border">
                <LocaleSwitcher />
                <DarkModeToggle />
              </div>
            </div>
          )}
        </div>
      </header>
      {renderInAppWarning()}
    </>
  );
}
